import { ViewChild, Input, Directive } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { IClassifier } from '../../models/Classifier';
import { AppService } from '../../services/app.service';
import { IFieldFile } from '../../models/IFieldFile';
import { Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { EmployeeApplicationService } from '../../services/employee-application.service';
import { IEmployee, IEmployeeApplicationCustomFileField, IEmployeeApplicationEditModel } from '../../models/EmployeeApplication';

export interface IFileMultipleField {
    title: string;
    config: IEmployeeApplicationCustomFileField;
    files?: IFieldFile[];
}

/**
 * Employee application form base component
 */
@Directive()
export abstract class EmployeeApplicationComponentBase {
    constructor(
        protected readonly app: AppService,
        protected readonly service: EmployeeApplicationService
    ) { }

    @Input() set type(value: 'string') {
        this.item.Type = value;
    }

    @Input() employee: IEmployee;

    @Output('init') initEvent = new EventEmitter<{ canDeactivate?: () => Observable<boolean> }>();
    @Output('done') doneEvent = new EventEmitter();

    protected readonly item: IEmployeeApplicationEditModel = <IEmployeeApplicationEditModel>{};

    files: File[] = [];
    customFiles: IFieldFile[];
    fileFieldRows: IFileMultipleField[] = [];
    showFileRequired: boolean;

    agreed: boolean;
    langEn: boolean;

    isError: boolean;
    isSuccess: boolean;

    readonly classifierDisplayValueFn = (item: IClassifier) => {
        return item ? this.langEn ? item.ValueEN : item.Value : '';
    }

    @ViewChild('form', { static: false }) protected form: NgForm;

    /**
     * Actions to take before submitting an application.
     */
    protected onSubmit(): boolean | Observable<boolean> {
        return of(true);
    }

    protected getFormErrors(): { label?: string, description?: string, error: string }[] {
        const errors: { label?: string, description?: string, error: string }[] = [];
        const valueError = this.app.translate('employeeApplication_fieldValueError');
        const required: string[] = [];
        const other: { label?: string, description?: string, error: string }[] = [];

        if (!this.form.valid) {
            const controls = this.form.controls;

            for (const name in controls) {
                const control = controls[name];

                if (control.errors) {
                    const input = document.querySelector(`[name="${name}"`)
                        || document.querySelector(`[ng-reflect-name="${name}"`);

                    if (input) {
                        const field = input.closest('.form-field');

                        if (field) {
                            const label = field.querySelector('.form-label')?.textContent
                                || this.app.translate(`employeeApplication_lbl${name[0].toUpperCase()}${name.substring(1)}`);

                            if (control.errors.required) {
                                required.push(label);
                            } else {
                                const error = field.querySelector('.invalid-feedback');

                                if (error) {
                                    other.push({ label, error: error.textContent });
                                } else {
                                    other.push({ label, error: valueError });
                                }
                            }
                        }
                    }
                }
            }
        }

        for (let i = 0; i < this.fileFieldRows.length; i++) {
            const field = this.fileFieldRows[i];
            const hasFileAdded = field.files.some(f => !!f.file);

            if (field.config.Required && !hasFileAdded) {
                this.showFileRequired = true;
                required.push(this.getFileFieldName(field));
            }
        }

        if (required.length) {
            errors.push({
                description: this.app.translate('employeeApplication_requiredFieldsEmpty'),
                error: `<ul><li>${required.join('</li><li>')}</li></ul>`
            });
        }

        if (other.length) {
            errors.push(...other);
        }

        if (!errors.length && !this.form.valid) {
            // failed to collect field errors - show generic warning at least
            errors.push({ error: this.app.translate('employeeApplication_formHasErrors') });
        }

        return errors;
    }

    submit() {
        const errors = this.getFormErrors();

        if (!this.agreed) {
            errors.push({ error: this.app.translate('employeeApplication_mustAgree') });
        }

        if (errors.length) {
            this.app.alert.error(errors.map(t => {
                const lbl = t.label ? `<div class="text-top">${t.label}</div>` : '';
                const desc = t.description ? `<div>${t.description}</div>` : '';

                return `<div class="mb-2">${lbl}${desc}${t.error}</div>`;
            }).join(''), this.app.translate('employeeApplication_notSubmittedTitle'));
        } else {
            const result = this.onSubmit();
            const done: Observable<boolean> = result instanceof Observable ? result : of(result);

            done.subscribe(result => {
                if (!result)
                    return;

                const files = this.customFiles ? this.customFiles.filter(t => t.file?.size).map(t => {
                    return {
                        data: t.file,
                        name: `${t.field}/////${t.file.name}`
                    }
                }) : this.files.map(t => {
                    return {
                        data: t
                    };
                });

                this.item.Upn = this.employee.Upn;
                this.item.StructureId = this.employee.StructureId;
                this.item.Files = files;

                this.app.addLoading(this.service.create(this.item)).subscribe(result => {
                        this.isSuccess = true;
                        this.doneEvent.emit();
                });
            });
        }
    }

    cancel() {
        this.app.navigateByUrl('/');
    }

    getFileFieldName(obj: IFileMultipleField) {
        const isEn = this.app.currentLanguage === 'en';
        return isEn ? obj.config.NameEN : obj.config.NameLV;
    }

    getFileExtensions(obj: IFileMultipleField) {
        if (!obj.config.Extensions || obj.config.Extensions == "") {
            return null;
        } else {
            return "." + obj.config.Extensions.split(',').join(',.');
        }
    }
}
